/*
 * Lifted Wishlist — localStorage-based, no Shopify customer accounts needed.
 *
 * Public API on window.LiftedWishlist:
 *   .get()                -> array of product handles
 *   .has(handle)          -> bool
 *   .toggle(handle)       -> new state (bool, true if now in list)
 *   .add(handle)
 *   .remove(handle)
 *   .clear()
 *
 * Auto-wires:
 *   [data-wishlist-toggle][data-product-handle] buttons — click toggles +
 *   syncs the .is-saved class so the heart fills/unfills.
 *   [data-wishlist-list] element — re-renders product cards via the
 *   /products/{handle}.js endpoint.
 */
(function() {
  const STORAGE_KEY = 'lifted_wishlist_v1';
  // Locale root ('/pl/' on Polish, '/' on default) so product fetches return the
  // localized title — /products/x.js is English, but /pl/products/x.js is Polish.
  const WLROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // i18n labels (snippets/lifted-i18n.liquid, inlined in <head> before this deferred file).
  // T(key, enFallback, repl) returns the translated label (with sentinel substitution) or
  // the EN fallback — so the JS-built wishlist UI never renders blank, and a missing config
  // degrades to English. Purchase (add-all) keys off variant ids, never these labels.
  const WT = (window.LIFTED_I18N || {}).wishlist || {};
  const wlT = window.liftedT || function (s) { return (s == null ? '' : '' + s); };
  function T(key, en, repl) { return wlT(WT[key], repl) || en; }

  function read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(h => typeof h === 'string') : [];
    } catch (e) {
      return [];
    }
  }
  function write(arr) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(arr)); }
    catch (e) {}
  }

  const api = {
    get() { return read(); },
    has(handle) { return read().indexOf(handle) >= 0; },
    add(handle) {
      const list = read();
      if (list.indexOf(handle) < 0) {
        list.push(handle);
        write(list);
        emit();
      }
    },
    remove(handle) {
      const list = read().filter(h => h !== handle);
      write(list);
      emit();
    },
    toggle(handle) {
      const list = read();
      const i = list.indexOf(handle);
      if (i >= 0) {
        list.splice(i, 1);
        write(list);
        emit();
        return false;
      }
      list.push(handle);
      write(list);
      emit();
      return true;
    },
    clear() { write([]); emit(); }
  };

  function emit() {
    document.dispatchEvent(new CustomEvent('lifted:wishlist:change', {
      detail: { handles: read() }
    }));
  }

  // Sync button states to current wishlist
  function syncButtons() {
    const list = read();
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const handle = btn.dataset.productHandle;
      if (!handle) return;
      const saved = list.indexOf(handle) >= 0;
      btn.classList.toggle('is-saved', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    });
    // Update count badges
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = list.length;
      if (list.length > 0) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
  }

  // Size pill click on wishlist page
  document.addEventListener('click', function(e) {
    const pill = e.target.closest('[data-wishlist-size]');
    if (!pill) return;
    e.preventDefault();
    e.stopPropagation();
    const row = pill.closest('[data-wishlist-size-row]');
    if (!row) return;
    const handle = row.dataset.productHandle;
    const size = pill.dataset.sizeValue;
    if (!handle || !size) return;
    const current = api.getSize(handle);
    const next = current === size ? null : size;
    api.setSize(handle, next);
    // Update pills
    row.querySelectorAll('[data-wishlist-size]').forEach(p => p.classList.remove('is-active'));
    if (next) pill.classList.add('is-active');
    const label = row.querySelector('.lifted-wishlist__size-label');
    if (label) label.textContent = next ? T('your_size', 'YOUR SIZE') : T('pick_a_size', 'PICK A SIZE');
  });

  // Click delegation
  document.addEventListener('click', function(e) {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    const nowSaved = api.toggle(handle);
    btn.classList.toggle('is-saved', nowSaved);
    btn.setAttribute('aria-pressed', nowSaved ? 'true' : 'false');
    // Persist saved size if the heart knows the currently-selected size
    // (PDP keeps data-current-size in sync with the active size chip).
    if (nowSaved) {
      const sz = btn.dataset.currentSize;
      if (sz) api.setSize(handle, sz);
      // Capture the localized product title shown at heart-time — the /products/.js
      // feed only returns the base English title, so the drawer would otherwise show English.
      if (btn.dataset.productTitle) api.setTitle(handle, btn.dataset.productTitle);
    } else {
      api.setSize(handle, null);
      api.setTitle(handle, null);
    }
    // Brief pop animation
    btn.classList.add('is-just-toggled');
    setTimeout(() => btn.classList.remove('is-just-toggled'), 320);
  });

  document.addEventListener('lifted:wishlist:change', syncButtons);

  const moneyFmt = (cents) => {
    // Use the active market currency (PLN on /pl, EUR on default…) + page locale,
    // so favorites match the rest of the storefront instead of a hardcoded €.
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    var amt = (cents / 100).toFixed(2).replace('.', ',');
    // Currency-driven (NOT page lang): the English-locale URLs can still run PLN, and
    // Intl('en','PLN') wrongly prints "PLN 37.99". Match the storefront's own format.
    if (cur === 'EUR') return '€' + amt;
    if (cur === 'PLN') return amt + ' zł';
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(cents / 100); }
    catch (e) { return amt + ' ' + cur; }
  };

  // Wishlist page renderer (full page grid)
  async function renderWishlistPage(container) {
    const handles = read();
    container.innerHTML = '';
    if (handles.length === 0) {
      container.innerHTML = '<div class="lifted-wishlist__empty"><p>' + T('empty_title', 'No favorites yet.') + '</p><a class="lifted-btn lifted-btn--primary" href="' + WLROOT + 'collections/all">' + T('empty_cta', 'SHOP THE DROP') + '</a></div>';
      return;
    }
    const grid = document.createElement('div');
    grid.className = 'lifted-wishlist__grid';
    container.appendChild(grid);
    const fetches = handles.map(handle =>
      fetch(WLROOT + 'products/' + encodeURIComponent(handle) + '.js')
        .then(r => r.ok ? r.json() : null)
        .catch(() => null)
    );
    const products = (await Promise.all(fetches)).filter(Boolean);
    if (products.length === 0) {
      container.innerHTML = '<div class="lifted-wishlist__empty"><p>' + T('unavailable', 'Saved items are no longer available.') + '</p><button class="lifted-btn lifted-btn--outline" onclick="window.LiftedWishlist.clear();location.reload();">' + T('clear', 'CLEAR FAVORITES') + '</button></div>';
      return;
    }
    products.forEach(p => {
      const card = document.createElement('div');
      card.className = 'lifted-wishlist__card';
      const savedSize = api.getSize(p.handle);
      // Find size option position (so we can render a size selector on the card)
      const sizeOpt = (p.options || []).findIndex(o => /size|rozmiar/i.test(typeof o === 'string' ? o : (o.name || '')));
      const sizes = sizeOpt >= 0
        ? [...new Set((p.variants || []).map(v => v.options[sizeOpt]).filter(Boolean))]
        : [];
      const variantBySize = sizeOpt >= 0
        ? (p.variants || []).reduce((acc, v) => { acc[v.options[sizeOpt]] = v; return acc; }, {})
        : {};
      const title = (p.title || api.getTitle(p.handle) || '').replace(/<\/?tc[^>]*>/gi, ''); // localized title (strip Transcy <tc> markers; stored title is a fallback)
      card.innerHTML = `
        <a class="lifted-wishlist__media" href="${p.url}">
          ${p.featured_image ? `<img src="${p.featured_image}" alt="${title}" loading="lazy">` : ''}
        </a>
        <button type="button" class="lifted-wishlist__remove" data-wishlist-toggle data-product-handle="${p.handle}" aria-label="${T('remove_from_favorites', 'Remove from favorites')}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12M6 18L18 6"/></svg>
        </button>
        <a class="lifted-wishlist__info" href="${p.url}">
          <span class="lifted-wishlist__name">${title}</span>
          <span class="lifted-wishlist__price">${moneyFmt(p.price)}</span>
        </a>
        ${sizes.length > 0 ? `
          <div class="lifted-wishlist__size-row" data-wishlist-size-row data-product-handle="${p.handle}">
            <span class="lifted-wishlist__size-label">${savedSize ? T('your_size', 'YOUR SIZE') : T('pick_a_size', 'PICK A SIZE')}</span>
            <div class="lifted-wishlist__size-pills">
              ${sizes.map(s => {
                const v = variantBySize[s];
                const avail = v && v.available;
                const active = savedSize === s ? 'is-active' : '';
                return `<button type="button" class="lifted-wishlist__size-pill ${active}" data-wishlist-size data-size-value="${s}"${avail ? '' : ' disabled'}>${s}</button>`;
              }).join('')}
            </div>
          </div>
        ` : ''}
      `;
      grid.appendChild(card);
    });
  }

  // Drawer renderer (slide-in from right, mounted globally via lifted-wishlist-drawer snippet)
  async function renderDrawer(list) {
    const handles = read();
    list.innerHTML = '';
    if (handles.length === 0) {
      list.innerHTML = '<p class="lifted-wld__empty">' + T('empty_title', 'No favorites yet.') + '<br><a href="' + WLROOT + 'collections/all">' + T('empty_cta_drawer', 'Shop the drop') + '</a></p>';
      return;
    }
    list.innerHTML = '<p class="lifted-wld__empty">' + T('loading_short', 'Loading...') + '</p>';
    const fetches = handles.map(handle =>
      fetch(WLROOT + 'products/' + encodeURIComponent(handle) + '.js')
        .then(r => r.ok ? r.json() : null)
        .catch(() => null)
    );
    const products = (await Promise.all(fetches)).filter(Boolean);
    list.innerHTML = '';
    if (products.length === 0) {
      list.innerHTML = '<p class="lifted-wld__empty">' + T('unavailable_drawer', 'Saved items no longer available.') + '</p>';
      return;
    }
    products.forEach(p => {
      const item = document.createElement('div');
      item.className = 'lifted-wld__item';
      const savedSize = api.getSize(p.handle);
      const title = (p.title || api.getTitle(p.handle) || '').replace(/<\/?tc[^>]*>/gi, ''); // localized title (strip Transcy <tc> markers; stored title is a fallback)
      item.innerHTML = `
        <a href="${p.url}"><img class="lifted-wld__item-img" src="${p.featured_image || ''}" alt="${title}" loading="lazy"></a>
        <div class="lifted-wld__item-info">
          <a href="${p.url}"><span class="lifted-wld__item-name">${title}</span></a>
          <span class="lifted-wld__item-price">${moneyFmt(p.price)}</span>
          ${savedSize ? `<span class="lifted-wld__item-size">${T('size_badge', 'SIZE ' + savedSize, { size: savedSize })}</span>` : ''}
        </div>
        <button type="button" class="lifted-wld__item-remove" data-wishlist-toggle data-product-handle="${p.handle}" aria-label="${T('remove', 'Remove')}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12M6 18L18 6"/></svg>
        </button>
      `;
      list.appendChild(item);
    });
  }

  function openDrawer() {
    const drawer = document.querySelector('[data-wld]');
    if (!drawer) return;
    const list = drawer.querySelector('[data-wld-list]');
    if (list) renderDrawer(list);
    drawer.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer() {
    const drawer = document.querySelector('[data-wld]');
    if (!drawer) return;
    drawer.setAttribute('hidden', '');
    document.body.style.overflow = '';
  }
  api.openDrawer = openDrawer;
  api.closeDrawer = closeDrawer;

  // Drawer click delegation. Note: the header heart link navigates to
  // /pages/wishlist (a real page) instead of opening the drawer. Drawer
  // is kept around as a programmatic fallback (window.LiftedWishlist.openDrawer())
  // but isn't auto-wired to the navbar heart.
  document.addEventListener('click', function(e) {
    if (e.target.closest('[data-wld-close]')) {
      closeDrawer();
      return;
    }
  });
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') closeDrawer();
  });

  // ---- SHARING ----
  // Encode the wishlist into a URL the user can copy/paste. Recipient
  // opens the URL, sees a one-click button to import the handles into
  // their own wishlist (merged with existing).
  function buildShareUrl() {
    const handles = read();
    if (handles.length === 0) return null;
    const url = new URL(WLROOT + 'pages/wishlist', window.location.origin);
    url.searchParams.set('share', handles.join(','));
    return url.toString();
  }
  api.share = async function() {
    const url = buildShareUrl();
    if (!url) return false;
    // Try Web Share API first (mobile-friendly, opens native share sheet)
    if (navigator.share) {
      try {
        await navigator.share({ title: T('native_share_title', 'My Lifted favorites'), url });
        return true;
      } catch (e) { /* user cancelled, fall through to clipboard */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      return 'copied';
    } catch (e) {
      // Last resort: prompt with the URL
      window.prompt(T('copy_link', 'Copy this link'), url);
      return 'manual';
    }
  };

  // On page load, look for ?share=handle1,handle2 and offer to import
  function checkIncomingShare() {
    const params = new URLSearchParams(window.location.search);
    const share = params.get('share');
    if (!share) return;
    const incomingHandles = share.split(',').map(h => h.trim()).filter(Boolean);
    if (incomingHandles.length === 0) return;
    const banner = document.querySelector('[data-wishlist-share-banner]');
    if (!banner) return;
    banner.removeAttribute('hidden');
    const countEl = banner.querySelector('[data-wishlist-share-count]');
    if (countEl) countEl.textContent = incomingHandles.length;
    const importBtn = banner.querySelector('[data-wishlist-share-import]');
    importBtn?.addEventListener('click', () => {
      incomingHandles.forEach(h => api.add(h));
      banner.setAttribute('hidden', '');
      // Clean ?share= out of the URL so refresh doesn't re-prompt
      const u = new URL(window.location.href);
      u.searchParams.delete('share');
      window.history.replaceState({}, '', u.toString());
    });
    banner.querySelector('[data-wishlist-share-dismiss]')?.addEventListener('click', () => {
      banner.setAttribute('hidden', '');
    });
  }

  // ---- SAVED SIZES ----
  // Storage shape: { handles: [...], sizes: { handle: "M" } }
  // Backwards-compatible: if existing storage is just an array, sizes is empty.
  const SIZES_KEY = 'lifted_wishlist_sizes_v1';
  function readSizes() {
    try {
      const raw = localStorage.getItem(SIZES_KEY);
      return raw ? (JSON.parse(raw) || {}) : {};
    } catch { return {}; }
  }
  function writeSizes(obj) {
    try { localStorage.setItem(SIZES_KEY, JSON.stringify(obj)); } catch {}
  }
  api.setSize = function(handle, size) {
    const s = readSizes();
    if (size) s[handle] = size; else delete s[handle];
    writeSizes(s);
  };
  api.getSize = function(handle) { return readSizes()[handle] || null; };

  // ---- SAVED TITLES ----
  // The /products/.js feed returns the BASE (English) title even on /pl, so the
  // drawer/page would show English names. Capture the localized title shown at
  // heart-time (set on the heart button as data-product-title, already <tc>-stripped)
  // and render that instead. Falls back to the fetched title for older saves / imports.
  const TITLES_KEY = 'lifted_wishlist_titles_v1';
  function readTitles() {
    try { const raw = localStorage.getItem(TITLES_KEY); return raw ? (JSON.parse(raw) || {}) : {}; }
    catch { return {}; }
  }
  function writeTitles(obj) { try { localStorage.setItem(TITLES_KEY, JSON.stringify(obj)); } catch {} }
  api.setTitle = function(handle, title) {
    const t = readTitles();
    if (title) t[handle] = title; else delete t[handle];
    writeTitles(t);
  };
  api.getTitle = function(handle) { return readTitles()[handle] || null; };

  // ---- ADD ALL TO CART ----
  // Walks every wishlist item, resolves its saved size to a variant id,
  // and adds them all in one /cart/add.js batch call. Skips items
  // missing a size, sold-out variants, and any items whose /products/.js
  // fetch fails. Returns a summary the caller can use to update UI.
  api.addAllToCart = async function () {
    const handles = read();
    if (handles.length === 0) return { added: 0, skipped: 0, total: 0 };
    const products = await Promise.all(handles.map(h =>
      fetch(WLROOT + 'products/' + encodeURIComponent(h) + '.js')
        .then(r => r.ok ? r.json() : null)
        .catch(() => null)
    ));
    const items = [];
    const skippedHandles = [];
    products.forEach((p, idx) => {
      if (!p) { skippedHandles.push(handles[idx]); return; }
      const sizeOptIdx = (p.options || []).findIndex(o =>
        /size|rozmiar/i.test(typeof o === 'string' ? o : (o.name || ''))
      );
      const savedSize = api.getSize(p.handle);
      let variant = null;
      if (sizeOptIdx >= 0) {
        // Product has a size option — need a saved size to pick a variant.
        if (!savedSize) { skippedHandles.push(p.handle); return; }
        variant = (p.variants || []).find(v =>
          v.available && v.options && v.options[sizeOptIdx] === savedSize
        );
      } else {
        // Single-variant (e.g. caps) — just take the first available.
        variant = (p.variants || []).find(v => v.available)
          || (p.variants || [])[0];
      }
      if (!variant || !variant.available) { skippedHandles.push(p.handle); return; }
      items.push({ id: variant.id, quantity: 1 });
    });
    if (items.length === 0) {
      return { added: 0, skipped: skippedHandles.length, total: handles.length, skippedHandles };
    }
    // Gather cart-items-component section ids so the cart drawer
    // morphs in fresh HTML once the add commits (mirrors what the
    // PDP / bestsellers ATC flows do).
    const sectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(c => {
      const id = c.dataset.sectionId;
      if (id && sectionIds.indexOf(id) === -1) sectionIds.push(id);
    });
    if (sectionIds.length === 0) sectionIds.push('lifted-cart-drawer');

    const body = { items: items, sections: sectionIds.join(','), sections_url: '/' };
    let addResp;
    try {
      addResp = await fetch('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      }).then(r => r.json());
    } catch (e) {
      return { added: 0, skipped: handles.length, total: handles.length, error: e };
    }
    if (addResp && addResp.status) {
      return { added: 0, skipped: handles.length, total: handles.length, error: addResp.description || addResp.message };
    }
    // Re-fetch cart so the toast + cart-bubble use the live total.
    let cart = null;
    try { cart = await fetch('/cart.js').then(r => r.json()); } catch (_) {}
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart || addResp,
        data: {
          source: 'lifted-wishlist-add-all',
          itemCount: items.length,
          sections: (addResp && addResp.sections) || {}
        }
      }
    }));
    if (typeof window.liftedForceDrawerRefresh === 'function') {
      setTimeout(window.liftedForceDrawerRefresh, 80);
    }
    return {
      added: items.length,
      skipped: skippedHandles.length,
      total: handles.length,
      skippedHandles
    };
  };

  // Auto-wire wishlist page (full grid) + drawer re-render on change
  function init() {
    syncButtons();
    checkIncomingShare();
    const pageList = document.querySelector('[data-wishlist-list]');
    if (pageList) {
      renderWishlistPage(pageList);
      document.addEventListener('lifted:wishlist:change', () => renderWishlistPage(pageList));
    }
    // Wire the SHARE button
    document.querySelectorAll('[data-wishlist-share-btn]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const result = await api.share();
        if (result === false) {
          // empty wishlist (buildShareUrl returned null)
          btn.textContent = T('nothing_to_share', 'NOTHING TO SHARE');
          setTimeout(() => { btn.textContent = T('share', 'SHARE WISHLIST'); }, 1800);
        } else {
          // result === true (native share sheet), 'copied' (clipboard), or 'manual' (prompt)
          const original = btn.textContent;
          btn.textContent = result === true ? T('shared', 'SHARED') : T('link_copied', 'LINK COPIED');
          setTimeout(() => { btn.textContent = original; }, 2200);
        }
      });
    });
    // Wire the ADD ALL TO CART button — visible only when the wishlist
    // has items. Disables the button while the batch /cart/add.js
    // request is in flight, then flashes "ADDED N" + cart update.
    function syncAddAllVisibility() {
      const btn = document.querySelector('[data-wishlist-add-all-btn]');
      if (!btn) return;
      if (read().length > 0) btn.removeAttribute('hidden');
      else btn.setAttribute('hidden', '');
    }
    syncAddAllVisibility();
    document.addEventListener('lifted:wishlist:change', syncAddAllVisibility);
    document.querySelectorAll('[data-wishlist-add-all-btn]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const label = btn.querySelector('[data-wishlist-add-all-label]');
        const original = label ? label.textContent : btn.textContent;
        btn.disabled = true;
        if (label) label.textContent = T('adding', 'ADDING…');
        try {
          const summary = await api.addAllToCart();
          btn.classList.add('is-success');
          if (label) {
            if (summary.added === 0 && summary.skipped > 0) {
              label.textContent = T('pick_sizes_first', 'PICK SIZES FIRST');
            } else if (summary.skipped > 0) {
              label.textContent = T('added_skipped', 'ADDED ' + summary.added + ' · ' + summary.skipped + ' SKIPPED', { added: summary.added, skipped: summary.skipped });
            } else {
              label.textContent = T('added', 'ADDED ' + summary.added, { count: summary.added });
            }
          }
        } catch (e) {
          if (label) label.textContent = T('try_again', 'TRY AGAIN');
        } finally {
          setTimeout(() => {
            btn.disabled = false;
            btn.classList.remove('is-success');
            if (label) label.textContent = original;
          }, 2400);
        }
      });
    });
    document.addEventListener('lifted:wishlist:change', () => {
      const drawer = document.querySelector('[data-wld]');
      if (drawer && !drawer.hasAttribute('hidden')) {
        const list = drawer.querySelector('[data-wld-list]');
        if (list) renderDrawer(list);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.LiftedWishlist = api;
})();
